import type { ConversationView } from '@/features/conversations/conversations.api';

/**
 * React Query key factory for the conversations resource.
 *
 * One canonical place so every consumer — the list hook, the row, the
 * mutation hooks, and (M5-T6) the WebSocket handler — invalidates against
 * the same shape. Tests should construct keys via this helper rather than
 * hand-rolling tuples.
 *
 * Shape:
 *   ['conversations', 'list', { accountId, view?, group?, search? }]
 *   ['conversations', 'detail', id]
 *   ['conversations', 'lock', id]
 *   ['conversations', 'profile', id]
 *
 * `accountId` is `null` for the aggregate "所有账号" list — every
 * account's conversations, filtered only by `view`/`search` — and a UUID for
 * a single-account list. The two are distinct cache entries.
 *
 * `view` is the flag-driven row filter (`'unread_pin'` | `'cuci'`, absent =
 * the "All" tab). The realtime projector ALSO reads it back off the key: a
 * list WITH a view keeps the classic pinned-then-recency ordering, a list
 * WITHOUT one mirrors the server's four-zone ordering — see
 * `projectMessageNewIntoLists` in `@/lib/realtime/projection`.
 *
 * The "search" field on the list key is SERVER-side now: `useConversations`
 * normalizes it (trim, empty → undefined) and passes the SAME value into
 * this key and into the request's `q=` param, so key and fetched content
 * correspond one-to-one — every search string is its own cache entry holding
 * exactly the rows the server filtered for it. (The field predates the
 * backend `q=` support; keeping it in the key from day one is what made the
 * server flip a no-reshape change.)
 */

export interface ConversationsListKeyParams {
  /** `null` = the aggregate "所有账号" list spanning every account. */
  accountId: string | null;
  /** Flag-driven row filter (`view=` query param). Absent = "All". */
  view?: ConversationView;
  /** `'phone'` in aggregate mode = same-phone grouping ordering (see
   *  `useConversations`); part of the key so the grouped and ungrouped lists
   *  are distinct cache entries. */
  group?: 'phone';
  /** Normalized search text (the request's `q=` param) — absent = no search.
   *  Always the trimmed non-empty form (see `useConversations`). */
  search?: string;
}

export const CONVERSATIONS_ROOT = 'conversations' as const;

export const conversationsKeys = {
  /** Root for `removeQueries({ queryKey: conversationsKeys.all })`. */
  all: [CONVERSATIONS_ROOT] as const,

  /** Root for any list query — useful as a prefix in `invalidateQueries`. */
  lists: () => [CONVERSATIONS_ROOT, 'list'] as const,

  /** Specific list query key for a given filter set. */
  list: (params: ConversationsListKeyParams) =>
    [CONVERSATIONS_ROOT, 'list', params] as const,

  /** Root for any detail query — prefix invalidation across all ids. */
  details: () => [CONVERSATIONS_ROOT, 'detail'] as const,

  /** Specific single-conversation query. */
  detail: (id: string) => [CONVERSATIONS_ROOT, 'detail', id] as const,

  /** Root for any lock query. */
  locks: () => [CONVERSATIONS_ROOT, 'lock'] as const,

  /** Lock fetched lazily per-row (the list response does not embed it). */
  lock: (id: string) => [CONVERSATIONS_ROOT, 'lock', id] as const,

  /** Root for any customer-profile query. */
  profiles: () => [CONVERSATIONS_ROOT, 'profile'] as const,

  /** Customer-profile sidebar aggregate for one conversation. */
  profile: (id: string) => [CONVERSATIONS_ROOT, 'profile', id] as const,

  /** Game-accounts mirror (Google Sheets, read-only) for one conversation. */
  gameAccounts: (id: string) => [CONVERSATIONS_ROOT, 'game-accounts', id] as const,
};
